import { Link } from "react-router-dom";
import {
  Flame,
  Youtube,
  Facebook,
  Instagram,
  Music2,
  Sparkles,
  CalendarClock,
  Send,
  ArrowRight,
  Mail,
  Globe,
} from "lucide-react";

const PLATFORMS = [
  { name: "YouTube", Icon: Youtube, hint: "Shorts" },
  { name: "Facebook", Icon: Facebook, hint: "Page Reels" },
  { name: "Instagram", Icon: Instagram, hint: "Reels" },
  { name: "TikTok", Icon: Music2, hint: "Videos" },
];

const PIPELINE = [
  {
    Icon: Flame,
    title: "Sermon clips",
    body: "Short, anointed moments are pulled from each weekly service we live-stream.",
  },
  {
    Icon: Sparkles,
    title: "Caption + tag",
    body: "Each clip gets a warm, faith-rooted caption and hashtags so the message reaches the right ears.",
  },
  {
    Icon: CalendarClock,
    title: "Scheduled posts",
    body: "Two clips a day, posted at the times when our community is most likely to be online.",
  },
  {
    Icon: Send,
    title: "Across every channel",
    body: "Delivered to YouTube, Facebook, Instagram, and TikTok — wherever the people you love spend their time.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 antialiased" data-testid="landing-page">
      {/* Subtle warm gradient backdrop */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-x-0 top-0 h-[60vh] bg-gradient-to-b from-amber-500/5 via-amber-500/0 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-[40vh] bg-gradient-to-t from-zinc-950 via-zinc-950 to-transparent" />
      </div>

      {/* Top bar */}
      <header className="border-b border-zinc-900/60 bg-zinc-950/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3" data-testid="landing-logo">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-400/10 ring-1 ring-amber-400/30">
              <Flame className="h-5 w-5 text-amber-400" />
            </div>
            <div className="leading-tight">
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-zinc-500">
                True Believer Fam
              </p>
              <p className="text-sm font-semibold">
                Auto<span className="text-amber-400">Clipper</span>
              </p>
            </div>
          </div>
          <Link
            to="/dashboard"
            data-testid="landing-header-dashboard-link"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:border-amber-500/40 hover:text-amber-300"
          >
            Open Dashboard <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="grid items-center gap-12 lg:grid-cols-[1.3fr,1fr]">
          <div>
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-zinc-400">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
              Internal Content Tool · True Believer Non-Denominational Body of Christ
            </p>
            <h1
              className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="landing-hero-heading"
            >
              True Believer Fam
              <br />
              <span className="text-amber-400">Auto-Clipper</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-zinc-300">
              Bringing the sermon to where the people are.
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400">
              An automated content delivery tool used by True Believer
              Non-Denominational Body of Christ to share sermon clips across our
              YouTube, Facebook, Instagram, and TikTok channels.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/dashboard"
                data-testid="landing-hero-cta"
                className="inline-flex items-center gap-2 rounded-md bg-amber-400 px-5 py-2.5 text-sm font-semibold text-zinc-950 shadow-lg shadow-amber-500/10 transition-all hover:bg-amber-300 hover:shadow-amber-500/20"
              >
                Open Dashboard <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="https://truebelieverfam.com"
                target="_blank"
                rel="noreferrer"
                data-testid="landing-hero-church-link"
                className="inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/40 px-5 py-2.5 text-sm font-medium text-zinc-300 transition-colors hover:border-zinc-700 hover:text-zinc-100"
              >
                <Globe className="h-4 w-4" /> Visit Our Church Website
              </a>
            </div>
          </div>

          {/* Hero symbol card */}
          <div className="relative mx-auto w-full max-w-sm">
            <div className="absolute -inset-4 rounded-3xl bg-amber-400/10 blur-3xl" />
            <div className="relative aspect-square overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/60 p-10 shadow-2xl shadow-amber-500/5">
              <svg viewBox="0 0 200 200" className="h-full w-full text-amber-400" fill="currentColor" aria-hidden="true">
                {/* Stylized flame */}
                <path d="M100 18 C 118 50, 144 60, 142 96 C 142 116, 128 132, 110 138 C 122 130, 126 118, 122 108 C 116 122, 104 128, 92 124 C 78 118, 72 102, 80 88 C 84 78, 96 70, 96 56 C 96 44, 90 36, 84 30 C 96 30, 100 26, 100 18 Z" opacity="0.95" />
                <path d="M100 70 C 110 86, 120 96, 116 114 C 114 124, 106 132, 96 132 C 86 132, 80 124, 80 114 C 80 102, 92 96, 92 84 C 92 78, 88 74, 86 70 C 92 70, 100 72, 100 70 Z" opacity="0.6" fill="#fff7d6"/>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* What it does */}
      <section className="border-y border-zinc-900/70 bg-zinc-950/60">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-amber-400/80">How it works</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              From the pulpit to your feed
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400">
              Every Sunday&apos;s message becomes a steady stream of short clips that
              meet people right where they already are — on their phones, on
              their lunch break, on the bus home.
            </p>
          </div>
          <ol
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="landing-pipeline-grid"
          >
            {PIPELINE.map((step, i) => (
              <li
                key={step.title}
                data-testid={`pipeline-step-${i + 1}`}
                className="group relative rounded-xl border border-zinc-800/80 bg-zinc-900/30 p-6 transition-colors hover:border-amber-500/30 hover:bg-zinc-900/60"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-400/10 ring-1 ring-amber-400/20 transition-colors group-hover:bg-amber-400/15">
                    <step.Icon className="h-5 w-5 text-amber-300" />
                  </div>
                  <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-zinc-500">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-zinc-100">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* For our congregation */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr,1.2fr] lg:items-center">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-amber-400/80">
              For our congregation
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              The Word, wherever you scroll
            </h2>
          </div>
          <div className="space-y-5 text-sm leading-relaxed text-zinc-300">
            <p>
              We post sermon clips twice a day across our platforms so members
              and seekers can engage with God&apos;s word wherever they spend time.
              Each clip carries the date of the original message, so anyone moved
              by what they see can look up the full sermon and sit under the
              teaching in its complete context.
            </p>
            <p className="text-zinc-400">
              This site is the internal control panel our media team uses to
              keep the steady flow of clips going. The public-facing fruit of
              that work lives on the platforms below.
            </p>
            <a
              href="https://truebelieverfam.com"
              target="_blank"
              rel="noreferrer"
              data-testid="landing-church-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-amber-300 hover:text-amber-200"
            >
              Visit Our Church Website <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="border-t border-zinc-900/70">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-amber-400/80">
              Where you&apos;ll find our clips
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              Follow along across all four
            </h2>
          </div>
          <ul
            className="mx-auto grid max-w-3xl gap-4 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="landing-platforms-grid"
          >
            {PLATFORMS.map((p) => (
              <li
                key={p.name}
                data-testid={`landing-platform-${p.name.toLowerCase()}`}
                className="flex flex-col items-center gap-2 rounded-xl border border-zinc-800/80 bg-zinc-900/30 px-6 py-7"
              >
                <p.Icon className="h-7 w-7 text-zinc-300" />
                <p className="text-sm font-semibold text-zinc-100">{p.name}</p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{p.hint}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="border-t border-zinc-900/70 bg-black/30"
        data-testid="landing-footer"
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-10 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-amber-400" />
              <p className="text-sm font-semibold text-zinc-100">
                True Believer Non-Denominational Body of Christ
              </p>
            </div>
            <a
              href="mailto:4thefam@truebelieverfam.com"
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-amber-300"
              data-testid="landing-contact-email"
            >
              <Mail className="h-3.5 w-3.5" /> 4thefam@truebelieverfam.com
            </a>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-400">
            <a
              href="https://truebelieverfam.com"
              target="_blank"
              rel="noreferrer"
              className="hover:text-amber-300"
              data-testid="landing-footer-church"
            >
              Church Website
            </a>
            <Link to="/terms" className="hover:text-amber-300" data-testid="landing-footer-terms">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-amber-300" data-testid="landing-footer-privacy">
              Privacy
            </Link>
            <Link to="/dashboard" className="hover:text-amber-300" data-testid="landing-footer-dashboard">
              Dashboard
            </Link>
          </nav>
        </div>
        <div className="border-t border-zinc-900/70">
          <p className="mx-auto max-w-6xl px-6 py-4 text-[11px] text-zinc-600">
            © 2026 True Believer Non-Denominational Body of Christ
          </p>
        </div>
      </footer>
    </div>
  );
}
